import React, { useState } from 'react';
import TimePicker from '../timePickerComponent/timePickerComponent';
import { AuthContext, MyContext } from '../context/AuthContext';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { Box, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { TimeSelection } from '../model/types';
import HourglassUpIcon from '../resource/HourglassUpIcon';
import HourglassDownIcon from '../resource/HourglassDownIcon';
import { DateTime } from 'luxon';
import { supabase } from '../supabaseClient';
import PermanenceIcon from '../resource/PermanenceIcon';
import confetti from 'canvas-confetti';
import { discountedWalletCost, getPowerHourStatus } from '../lib/powerHour';
import { hapticSuccess, hapticHeavy, hapticWarning, hapticError } from '../lib/haptics';
import { playUp, playDown, playMilestone, playError } from '../lib/sounds';

function fireImmortalConfetti() {
  // Golden fireworks burst for immortal posts
  const end = Date.now() + 1200;
  const colors = ['#FFD700', '#FFA500', '#FFFFFF', '#FFE066'];
  const frame = () => {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors,
      zIndex: 9999,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors,
      zIndex: 9999,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
}

function VoteButton({
  objectType,
  parentID,
  giveTime,
  onSubmit,
  makePermanent = false,
}: {
  objectType: string;
  parentID: string;
  giveTime?: boolean;
  makePermanent?: boolean;
  onSubmit?: () => void;
}) {
  const getPost = React.useContext<CacheContextType>(CacheContext).getPost;
  const getFollow = React.useContext<CacheContextType>(CacheContext).getFollow;
  const [showClock, setShowClock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const ref = React.useRef(null);
  const userRecord = React.useContext<MyContext>(AuthContext).userRecord;
  const [voteTime, setVoteTime] = useState<TimeSelection>({
    days: 0,
    hours: 0,
    minutes: 0,
  });

  const handleVote = async (isPermanent: boolean = false) => {
    if (!userRecord) {
      setError('You need to be logged in to vote');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let timeValue =
        voteTime.days * 24 * 60 * 60 +
        voteTime.hours * 60 * 60 +
        voteTime.minutes * 60;

      if (timeValue === 0 && !isPermanent) {
        setError('Please select an amount of time');
        setLoading(false);
        return;
      }

      // Power Hour: the post still moves by `timeValue` (the network gets
      // full value), but the voter only pays half from their wallet.
      const walletCost = isPermanent ? 0 : discountedWalletCost(timeValue);
      const powerHourActive = getPowerHourStatus().isActive;

      if (!userRecord) {
        setError('Sign in to vote');
        setLoading(false);
        return;
      }
      if (userRecord.dying_at) {
        setError('Your account is in purgatory — votes are frozen');
        hapticWarning();
        setLoading(false);
        return;
      }
      if (isPermanent && userRecord.permanence_tokens < 1) {
        setError('Not enough permanence tokens');
        hapticWarning();
        setLoading(false);
        return;
      } else if (walletCost > userRecord.time_wallet_seconds) {
        setError('Not enough time in your wallet');
        hapticWarning();
        setLoading(false);
        return;
      }

      const objectRecord =
        objectType === 'posts' ? getPost(parentID) : getFollow(parentID);

      if (!objectRecord) {
        setError(`Could not find the ${objectType.slice(0, -1)}`);
        setLoading(false);
        return;
      }

      const objectExpirySecs = DateTime.fromISO(objectRecord.expire_at).toSeconds();
      const wasExpired = DateTime.now().toSeconds() > objectExpirySecs;
      const signedTimeValue = giveTime ? timeValue : -timeValue;
      const newExpirySecs = objectExpirySecs + (isPermanent ? 99999999990 : signedTimeValue);
      const newExpiryISO = DateTime.fromSeconds(newExpirySecs).toISO();

      const revivedPost = wasExpired && DateTime.now().toSeconds() < newExpirySecs;
      const isExpired = DateTime.now().toSeconds() > newExpirySecs && !isPermanent;
      const killedPost = !wasExpired && isExpired;

      // 1. Insert vote
      const { error: voteError } = await supabase.from('votes').insert({
        user_record_id: userRecord.id,
        object_type: objectType,
        parent_id: parentID,
        time_value: signedTimeValue,
        revived: revivedPost,
        killed: killedPost,
        made_permanent: isPermanent,
        is_test_mode: false,
      });

      if (voteError) throw voteError;

      // 2. Update the voted-on object
      const { error: objectError } = await supabase.from(objectType).update({
        expire_at: newExpiryISO,
        is_permanent: isPermanent,
        is_expired: isExpired,
      }).eq('id', parentID);

      if (objectError) throw objectError;

      // 3. Deduct voter's wallet (walletCost is halved during Power Hour)
      const newWalletSeconds = userRecord.time_wallet_seconds - walletCost;
      const { error: walletError } = await supabase.from('users').update({
        time_wallet_seconds: isPermanent ? userRecord.time_wallet_seconds : newWalletSeconds,
        permanence_tokens: isPermanent
          ? userRecord.permanence_tokens - 1
          : userRecord.permanence_tokens,
      }).eq('id', userRecord.id);

      if (walletError) throw walletError;

      // Note: crediting the object author's wallet on upvote is handled by the DB trigger on_vote_credit_wallet

      // Fire a floating time-delta indicator next to the wallet chip
      if (!isPermanent) {
        window.dispatchEvent(new CustomEvent('wipe-time-delta', { detail: { seconds: -walletCost } }));
      }

      if (isPermanent) {
        fireImmortalConfetti();
        hapticHeavy();
        playMilestone();
      } else {
        hapticSuccess();
        if (giveTime) playUp();
        else playDown();
      }

      setSuccess(
        isPermanent
          ? '✨ IMMORTAL! Post lives forever!'
          : powerHourActive
            ? `⚡ ${giveTime ? 'Gave' : 'Took'} time at 50% OFF!`
            : `${giveTime ? 'Gave' : 'Took'} time successfully!`,
      );

      onSubmit && onSubmit();
    } catch (err) {
      console.error('Error in vote operation:', err);
      setError('Failed to process your vote. Please try again.');
      hapticError();
      playError();
    } finally {
      setLoading(false);
      if (showClock) {
        setShowClock(false);
      }
    }
  };

  const toggleClock = () => {
    setShowClock((prev) => !prev);
  };

  const openTimePicker = () => {
    if (userRecord) {
      const defaultVoteTime = {
        days: userRecord.user_settings?.voteDays || 0,
        hours: userRecord.user_settings?.voteHours || 1,
        minutes: userRecord.user_settings?.voteMinutes || 0,
      };
      if (
        voteTime.days !== defaultVoteTime.days ||
        voteTime.hours !== defaultVoteTime.hours ||
        voteTime.minutes !== defaultVoteTime.minutes
      ) {
        setVoteTime(defaultVoteTime);
      }
    }
    setShowClock(true);
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Box
      sx={{
        color: 'text.primary',
        position: 'relative',
      }}
    >
      {makePermanent ? (
        <Tooltip title="Make permanent">
          <span>
            <IconButton
              onClick={() => handleVote(true)}
              disabled={loading}
              color={loading ? 'default' : 'primary'}
            >
              <PermanenceIcon
                style={{
                  width: '25px',
                  height: '25px',
                  opacity: loading ? 0.5 : 1,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={`Click to ${giveTime ? 'give' : 'take'} time`}>
          <span>
            <IconButton
              onClick={openTimePicker}
              ref={ref}
              sx={{
                touchAction: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
              disabled={loading}
              color={loading ? 'default' : 'primary'}
            >
              {giveTime ? (
                <HourglassUpIcon
                  style={{
                    width: '25px',
                    height: '25px',
                    opacity: loading ? 0.5 : 1,
                  }}
                />
              ) : (
                <HourglassDownIcon
                  style={{
                    width: '25px',
                    height: '25px',
                    opacity: loading ? 0.5 : 1,
                  }}
                />
              )}
            </IconButton>
            <TimePicker
              time={voteTime}
              setTime={setVoteTime}
              submit={handleVote}
              showClock={showClock}
              toggleClock={toggleClock}
              parentRef={ref}
            />
          </span>
        </Tooltip>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={handleCloseSnackbar}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleCloseSnackbar}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default VoteButton;
